import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowBack, ReceiptLongOutlined } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { searchCustomers } from "../../api/customerApi";
import { getAllGoods } from "../../api/goodsApi";
import NoteSelect from "../../components/NoteSelect";
import backgroundPattern from '../../assets/pasbookpng.png';
import "./creditnote.css";

const STORAGE_KEY = "creditNotes";
const today = () => new Date().toISOString().slice(0, 10);

const emptyItem = () => ({
  id: `${Date.now()}-${Math.random()}`,
  item: "",
  account: "Sales",
  qty: 1,
  rate: 0,
  discount: 0,
});

const safeArray = (value) => (Array.isArray(value) ? value : []);

const money = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function CustomerPicker({ value, onChange }) {
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [style, setStyle] = useState(null);
  const [localCustomers, setLocalCustomers] = useState([]);

  const customerName = (customer) =>
    customer?.name ||
    customer?.displayName ||
    customer?.customer_name ||
    customer?.customerName ||
    "";

  useEffect(() => {
    try {
      setLocalCustomers(safeArray(JSON.parse(localStorage.getItem("customers") || "[]")));
    } catch {
      setLocalCustomers([]);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const response = await searchCustomers({ name: query.trim() });
        const apiRows = Array.isArray(response?.data)
          ? response.data
          : safeArray(response?.data?.data || response?.data?.customers);

        const q = query.trim().toLowerCase();
        const merged = [...apiRows, ...localCustomers].filter((row) =>
          customerName(row).toLowerCase().includes(q)
        );

        const seen = new Set();
        setCustomers(
          merged.filter((row) => {
            const key = String(row.id ?? customerName(row)).toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return Boolean(customerName(row));
          })
        );
      } catch {
        const q = query.trim().toLowerCase();
        setCustomers(
          localCustomers.filter((row) =>
            customerName(row).toLowerCase().includes(q)
          )
        );
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => window.clearTimeout(timer);
  }, [open, query, localCustomers]);

  useEffect(() => {
    if (!open || !triggerRef.current) return undefined;

    const update = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      const width = Math.min(Math.max(rect.width, 320), window.innerWidth - 24);
      const left = Math.max(
        12,
        Math.min(rect.left, window.innerWidth - width - 12)
      );
      const below = window.innerHeight - rect.bottom - 12;
      const above = rect.top - 12;
      const showAbove = below < 260 && above > below;
      const maxHeight = Math.max(
        160,
        Math.min(300, showAbove ? above : below)
      );

      setStyle({
        position: "fixed",
        left,
        top: showAbove ? rect.top - maxHeight - 6 : rect.bottom + 6,
        width,
        maxHeight,
        zIndex: 10050,
      });
    };

    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, customers.length]);

  useEffect(() => {
    const close = (event) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const list = open && style ? (
    <div className="note-customer-dropdown" style={style} ref={dropdownRef}>
      <div className="note-customer-search">
        <input
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search customer..."
        />
      </div>

      {loading && <div className="note-dropdown-state">Searching...</div>}

      {!loading && customers.length === 0 && (
        <div className="note-dropdown-state">No customers found.</div>
      )}

      {!loading &&
        customers.map((customer, index) => (
          <button
            type="button"
            className="note-customer-option"
            key={customer.id ?? `${customerName(customer)}-${index}`}
            onClick={() => {
              onChange(customerName(customer), customer);
              setOpen(false);
              setQuery("");
            }}
          >
            <strong>{customerName(customer)}</strong>
            <span>
              {customer.contactNumber ||
                customer.phone ||
                customer.email ||
                "Customer"}
            </span>
          </button>
        ))}

      <button
        type="button"
        className="note-add-customer"
        onClick={() => {
          setOpen(false);
          window.location.assign("/dashboard/customers/new");
        }}
      >
        + Add New Customer
      </button>
    </div>
  ) : null;

  return (
    <>
      <div className="note-customer-picker" ref={triggerRef}>
        <button
          type="button"
          className={`note-field note-customer-trigger ${
            value ? "has-value" : ""
          }`}
          onClick={() => setOpen((current) => !current)}
        >
          <span>{value || "Select or add a customer"}</span>
          <span className="note-chevron" aria-hidden="true">⌄</span>
        </button>
      </div>
      {typeof document !== "undefined" &&
        createPortal(list, document.body)}
    </>
  );
}

function NotePage({ noteType }) {
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state?.editData || null;
  const storageKey = noteType === "Credit" ? STORAGE_KEY : "debitNotes";
  const prefix = noteType === "Credit" ? "CN" : "DN";

  const [customer, setCustomer] = useState(editData?.customer || "");
  const [customerId, setCustomerId] = useState(editData?.customerId || null);
  const [noteNo, setNoteNo] = useState(
    editData?.noteNo || editData?.creditNoteNo || `${prefix}-00001`
  );
  const [referenceNo, setReferenceNo] = useState(
    editData?.referenceNo || editData?.refNo || ""
  );
  const [date, setDate] = useState(editData?.date || today());
  const [salesperson, setSalesperson] = useState(editData?.salesperson || "");
  const [subject, setSubject] = useState(editData?.subject || "");
  const [customerNotes, setCustomerNotes] = useState(
    editData?.customerNotes || ""
  );
  const [terms, setTerms] = useState(editData?.terms || "");
  const [taxType, setTaxType] = useState(editData?.taxType || "GST");
  const [tax, setTax] = useState(editData?.tax || "GST 18%");
  const [taxAmount, setTaxAmount] = useState(
    editData?.taxAmount ?? editData?.taxValue ?? 0
  );
  const [taxAuto, setTaxAuto] = useState(
    editData?.taxAmount == null && editData?.taxValue == null
  );
  const [adjustment, setAdjustment] = useState(editData?.adjustment ?? 0);
  const [items, setItems] = useState(
    safeArray(editData?.items).length
      ? editData.items.map((row) => ({
          ...row,
          discount: row.discount ?? row.disc ?? 0,
        }))
      : [emptyItem()]
  );
  const [goods, setGoods] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [selectedGoods, setSelectedGoods] = useState([]);
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    getAllGoods()
      .then((response) => {
        if (!active) return;
        const rows = Array.isArray(response?.data)
          ? response.data
          : safeArray(response?.data?.data || response?.data?.goods);
        setGoods(rows);
      })
      .catch(() => {
        if (active) setGoods([]);
      });
    return () => {
      active = false;
    };
  }, []);

  const goodsOptions = useMemo(
    () =>
      goods
        .map((row) => ({
          id: row.id ?? row.goodsId ?? row.itemId ?? row.name,
          name:
            row.name ||
            row.itemName ||
            row.goodsName ||
            row.description ||
            "",
          rate: Number(row.rate ?? row.sellingPrice ?? row.price ?? 0),
          account: row.accountName || row.account || "Sales",
        }))
        .filter((row) => row.name),
    [goods]
  );

  const subtotalValue = useMemo(
    () =>
      items.reduce(
        (sum, row) =>
          sum +
          Number(row.qty || 0) * Number(row.rate || 0) -
          Number(row.discount || 0),
        0
      ),
    [items]
  );

  const taxRate = useMemo(() => {
    if (tax === "No Tax") return 0;
    const match = String(tax || "").match(/(\d+(?:\.\d+)?)\s*%/);
    return match ? Number(match[1]) / 100 : 0;
  }, [tax]);

  const totals = useMemo(() => {
    const subtotal = subtotalValue;
    const round2 = (value) => Math.round(value * 100) / 100;
    const taxValue = taxAuto ? round2(subtotal * taxRate) : Number(taxAmount || 0);
    const taxEffect =
      taxType === "TDS" ? -taxValue : taxType === "GST" || taxType === "TCS" ? taxValue : 0;

    return {
      subtotal,
      taxValue,
      taxEffect,
      total: Math.max(0, round2(subtotal + taxEffect + Number(adjustment || 0))),
    };
  }, [subtotalValue, taxAuto, taxRate, taxAmount, taxType, adjustment]);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 2600);
  };

  const updateItem = (id, field, value) => {
    setItems((rows) =>
      rows.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const addRow = () => setItems((rows) => [...rows, emptyItem()]);

  const removeRow = (id) => {
    if (items.length === 1) {
      showToast("At least one item row is required.", "error");
      return;
    }
    setItems((rows) => rows.filter((row) => row.id !== id));
  };


  const addBulkItems = () => {
    const rows = selectedGoods.map((goodsId) => {
      const selected = goodsOptions.find(
        (row) => String(row.id) === String(goodsId)
      );
      return {
        ...emptyItem(),
        item: selected.name,
        rate: selected.rate,
        account: selected.account,
      };
    });

    if (rows.length) {
      setItems((current) => [...current, ...rows]);
      showToast(`${rows.length} item(s) added.`);
    }
    setSelectedGoods([]);
    setBulkOpen(false);
  };

  const save = (status) => {
    if (!customer.trim()) {
      showToast("Customer Name is required.", "error");
      return;
    }
    if (!date) {
      showToast(`${noteType} Note Date is required.`, "error");
      return;
    }
    const validItems = items.filter((row) => String(row.item || "").trim());
    if (!validItems.length) {
      showToast("Add at least one item before saving.", "error");
      return;
    }

    setSaving(true);

    let existing = [];
    try {
      existing = safeArray(JSON.parse(localStorage.getItem(storageKey) || "[]"));
    } catch {
      existing = [];
    }

    const payload = {
      id: editData?.id || Date.now(),
      type: noteType,
      customer,
      customerId,
      noteNo,
      creditNoteNo: noteNo,
      referenceNo,
      refNo: referenceNo,
      date,
      salesperson,
      subject,
      customerNotes,
      terms,
      taxType,
      tax,
      taxAmount: Number(totals.taxValue || 0),
      adjustment: Number(adjustment || 0),
      items: validItems,
      subTotal: totals.subtotal,
      total: totals.total,
      status,
      updatedAt: new Date().toISOString(),
    };

    const next = editData
      ? existing.map((row) => (row.id === editData.id ? payload : row))
      : [...existing, payload];

    localStorage.setItem(storageKey, JSON.stringify(next));
    showToast(
      editData
        ? `${noteType} Note updated successfully.`
        : status === "draft"
        ? `${noteType} Note saved as draft.`
        : `${noteType} Note saved successfully.`
    );

    window.setTimeout(() => navigate(`/dashboard/${noteType.toLowerCase()}-notes`), 650);
  };

  return (
    <div className="note-page">
      <div
        className="note-background-watermark"
        style={{ backgroundImage: `url(${backgroundPattern})` }}
      />
      {toast && (
        <div className={`note-toast ${toast.type}`} role="status">
          <span>{toast.type === "success" ? "✓" : "!"}</span>
          {toast.message}
        </div>
      )}

      <header className="note-header">
        <div className="note-header-left">
          <div className="note-page-icon" aria-hidden="true">
            <ReceiptLongOutlined />
          </div>
          <div className="note-page-title-wrap">
            <h1>{editData ? "Edit" : "New"} {noteType} Note</h1>
            <p>Create a professional {noteType.toLowerCase()} note for your customer.</p>
          </div>
        </div>
        <button
          type="button"
          className="note-header-back"
          onClick={() => navigate(`/dashboard/${noteType.toLowerCase()}-notes`)}
          aria-label={`Back to ${noteType.toLowerCase()} notes`}
        >
          <ArrowBack />
        </button>
      </header>

      <main className="note-shell">
        <section className="note-card">
          <div className="note-section-head">
            <div>
              <h2>Note details</h2>
              <p>Enter the customer and document information.</p>
            </div>
            <span className="note-required">* Required</span>
          </div>

          <div className="note-form-grid">
            <label className="note-field-group">
              <span>Customer Name <b>*</b></span>
              <CustomerPicker
                value={customer}
                onChange={(name, row) => {
                  setCustomer(name);
                  setCustomerId(row?.id ?? row?.customerId ?? null);
                }}
              />
            </label>

            <label className="note-field-group">
              <span>{noteType} Note # <b>*</b></span>
              <input
                className="note-field"
                value={noteNo}
                onChange={(event) => setNoteNo(event.target.value)}
                placeholder={`${prefix}-00001`}
              />
            </label>

            <label className="note-field-group">
              <span>Reference #</span>
              <input
                className="note-field"
                value={referenceNo}
                onChange={(event) => setReferenceNo(event.target.value)}
                placeholder="Optional reference number"
              />
            </label>

            <label className="note-field-group">
              <span>{noteType} Note Date <b>*</b></span>
              <input
                type="date"
                className="note-field"
                value={date}
                onChange={(event) => setDate(event.target.value)}
              />
            </label>

            <label className="note-field-group">
              <span>Salesperson</span>
              <NoteSelect
                value={salesperson}
                onChange={setSalesperson}
                placeholder="Select salesperson"
                options={[
                  { value: "1", label: "Salesperson 1" },
                  { value: "2", label: "Salesperson 2" },
                ]}
              />
            </label>

            <label className="note-field-group note-field-wide">
              <span>Subject</span>
              <textarea
                className="note-field note-textarea"
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                placeholder={`Reason for this ${noteType.toLowerCase()} note`}
              />
            </label>
          </div>
        </section>

        <section className="note-card note-items-card">
          <div className="note-section-head">
            <div>
              <h2>Items & services</h2>
              <p>Add products, quantities, rates and discounts.</p>
            </div>
            <button
              type="button"
              className="note-link-btn"
              onClick={() => setBulkOpen(true)}
              disabled={!goodsOptions.length}
            >
              + Add items in bulk
            </button>
          </div>

          <div className="note-table-wrap">
            <table className="note-table">
              <thead>
                <tr>
                  <th className="col-item">ITEM DETAILS</th>
                  <th className="col-account">ACCOUNT</th>
                  <th className="col-number">QTY</th>
                  <th className="col-number">RATE</th>
                  <th className="col-number">DISCOUNT</th>
                  <th className="col-amount">AMOUNT</th>
                  <th className="col-action" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {items.map((row) => {
                  const amount =
                    Number(row.qty || 0) * Number(row.rate || 0) -
                    Number(row.discount || 0);

                  return (
                    <tr key={row.id}>
                      <td>
                        <div className="note-item-picker">
                          <input
                            className="note-table-input"
                            value={row.item}
                            onChange={(event) =>
                              updateItem(row.id, "item", event.target.value)
                            }
                            list={`goods-${noteType}`}
                            placeholder="Type or select an item"
                          />
                          <datalist id={`goods-${noteType}`}>
                            {goodsOptions.map((option) => (
                              <option key={option.id} value={option.name} />
                            ))}
                          </datalist>
                        </div>
                      </td>
                      <td>
                        <NoteSelect
                          className="note-table-input"
                          value={row.account}
                          onChange={(value) => updateItem(row.id, "account", value)}
                          options={[
                            { value: "Sales", label: "Sales" },
                            { value: "Discount", label: "Discount" },
                            { value: "General Income", label: "General Income" },
                            { value: "Other", label: "Other" },
                          ]}
                        />
                      </td>
                      <td>
                        <input
                          className="note-table-input number"
                          type="number"
                          min="0"
                          step="0.01"
                          value={row.qty}
                          onChange={(event) =>
                            updateItem(row.id, "qty", event.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="note-table-input number"
                          type="number"
                          min="0"
                          step="0.01"
                          value={row.rate}
                          onChange={(event) =>
                            updateItem(row.id, "rate", event.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="note-table-input number"
                          type="number"
                          min="0"
                          step="0.01"
                          value={row.discount}
                          onChange={(event) =>
                            updateItem(row.id, "discount", event.target.value)
                          }
                        />
                      </td>
                      <td className="note-amount">{money(amount)}</td>
                      <td>
                        <button
                          type="button"
                          className="note-remove-btn"
                          onClick={() => removeRow(row.id)}
                          aria-label="Remove item"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <button type="button" className="note-add-row" onClick={addRow}>
            + Add new row
          </button>

          <div className="note-lower-grid">
            <label className="note-field-group">
              <span>Customer Notes</span>
              <textarea
                className="note-field note-textarea large"
                value={customerNotes}
                onChange={(event) => setCustomerNotes(event.target.value)}
                placeholder={`Notes that will appear on the ${noteType.toLowerCase()} note`}
              />
            </label>

            <div className="note-total-card">
              <div className="note-total-row">
                <span>Sub Total</span>
                <strong>₹ {money(totals.subtotal)}</strong>
              </div>

              <div className="note-tax-controls">
                <div className="note-tax-types">
                  {["GST", "TDS", "TCS"].map((type) => (
                    <label key={type}>
                      <input
                        type="radio"
                        name={`${noteType}-tax-type`}
                        checked={taxType === type}
                        onChange={() => setTaxType(type)}
                      />
                      {type}
                    </label>
                  ))}
                </div>

                <NoteSelect
                  className="note-tax-select"
                  value={tax}
                  onChange={(value) => {
                    setTax(value);
                    setTaxAuto(true);
                  }}
                  options={[
                    { value: "GST 18%", label: "GST 18%" },
                    { value: "GST 12%", label: "GST 12%" },
                    { value: "GST 5%", label: "GST 5%" },
                    { value: "TDS 10%", label: "TDS 10%" },
                    { value: "TCS 1%", label: "TCS 1%" },
                    { value: "No Tax", label: "No Tax" },
                  ]}
                />

                <input
                  className="note-tax-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={taxAuto ? totals.taxValue : taxAmount}
                  onChange={(event) => {
                    setTaxAmount(event.target.value);
                    setTaxAuto(false);
                  }}
                />
              </div>

              <label className="note-total-row note-adjustment">
                <span>Adjustment</span>
                <input
                  className="note-adjustment-input"
                  type="number"
                  step="0.01"
                  value={adjustment}
                  onChange={(event) => setAdjustment(event.target.value)}
                />
              </label>

              <div className="note-total-row note-grand-total">
                <span>Total</span>
                <strong>₹ {money(totals.total)}</strong>
              </div>
            </div>
          </div>
        </section>

        <section className="note-card">
          <div className="note-section-head compact">
            <div>
              <h2>Terms & conditions</h2>
              <p>Optional terms displayed on the document.</p>
            </div>
          </div>
          <textarea
            className="note-field note-textarea"
            value={terms}
            onChange={(event) => setTerms(event.target.value)}
            placeholder="Enter terms and conditions"
          />
        </section>

        <footer className="note-actions">
          <button
            type="button"
            className="note-btn secondary"
            onClick={() => navigate(`/dashboard/${noteType.toLowerCase()}-notes`)}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="note-btn secondary"
            onClick={() => save("draft")}
            disabled={saving}
          >
            Save as draft
          </button>
          <button
            type="button"
            className="note-btn primary"
            onClick={() => save("open")}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save & send"}
          </button>
        </footer>
      </main>

      {bulkOpen && (
        <div className="note-modal-backdrop" onMouseDown={() => setBulkOpen(false)}>
          <div className="note-modal" onMouseDown={(event) => event.stopPropagation()}>
            <div className="note-modal-head">
              <div>
                <h3>Add items in bulk</h3>
                <p>Select products from inventory.</p>
              </div>
              <button type="button" onClick={() => setBulkOpen(false)}>×</button>
            </div>

            <div className="note-bulk-list">
              {goodsOptions.length === 0 && (
                <div className="note-dropdown-state">No inventory items available.</div>
              )}
              {goodsOptions.map((option) => (
                <label className="note-bulk-option" key={option.id}>
                  <input
                    type="checkbox"
                    checked={selectedGoods.includes(String(option.id))}
                    onChange={(event) => {
                      const id = String(option.id);
                      setSelectedGoods((current) =>
                        event.target.checked
                          ? [...current, id]
                          : current.filter((value) => value !== id)
                      );
                    }}
                  />
                  <span>
                    <strong>{option.name}</strong>
                    <small>₹ {money(option.rate)}</small>
                  </span>
                </label>
              ))}
            </div>

            <div className="note-modal-actions">
              <button type="button" className="note-btn secondary" onClick={() => setBulkOpen(false)}>
                Cancel
              </button>
              <button type="button" className="note-btn primary" onClick={addBulkItems}>
                Add {selectedGoods.length || ""} item{selectedGoods.length === 1 ? "" : "s"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CreditNote() {
  return <NotePage noteType="Credit" />;
}
